import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { Calendar, Link2, X, Check, CreditCard, Loader2, LogOut } from 'lucide-react';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ConversationsPage from './pages/ConversationsPage';
import ConversationDetailPage from './pages/ConversationDetailPage';
import ApplicantsPage from './pages/ApplicantsPage';
import FormsPage from './pages/FormsPage';
import FormBuilderPage from './pages/FormBuilderPage';
import FormSubmissionsPage from './pages/FormSubmissionsPage';
import EmailPage from './pages/EmailPage';
import SettingsPage from './pages/SettingsPage';
import SupportPage from './pages/SupportPage';

const API_URL = import.meta.env.VITE_API_URL || '';

const GRANDFATHERED_EMAILS = ['seefeldmaxwell1@gmail.com'];

// ─── Paywall Screen ───────────────────────────────────────────────────────────
function PaywallPage({ onCheckout, isPending, onLogout }: { onCheckout: () => void; isPending: boolean; onLogout: () => void }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-blue-600" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-blue-600 rounded-2xl mb-4 shadow-lg shadow-primary-500/25">
            <CreditCard className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Start your free trial</h1>
          <p className="mt-2 text-gray-500">14 days free, then $25/month. Cancel anytime.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <span className="text-4xl font-bold text-gray-900">$25</span>
            <span className="text-gray-500 text-lg">/month</span>
            <p className="text-sm text-primary-600 font-semibold mt-1">14-day free trial included</p>
          </div>

          <ul className="space-y-3 mb-8">
            {[
              'Unlimited application forms',
              'Unlimited submissions & applicants',
              'Email campaigns to candidates',
              'Custom branding (logo, colors)',
              'Resume & video uploads',
              'Calendly integration',
              'Indeed & LinkedIn ready',
              'Real-time notifications',
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-3 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          <button
            onClick={onCheckout}
            disabled={isPending}
            className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isPending ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to checkout...</>
            ) : (
              'Start 14-Day Free Trial'
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-4">
            You won't be charged until your trial ends. Cancel anytime from your dashboard.
          </p>
        </div>

        <button
          onClick={onLogout}
          className="flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-gray-600 mx-auto mt-6"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}

// ─── Calendly Prompt Modal ────────────────────────────────────────────────────
function CalendlyPromptModal({ onSave, onDismiss }: { onSave: (link: string) => void; onDismiss: () => void }) {
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!calendlyUrl.trim()) return;
    setSaving(true);
    await onSave(calendlyUrl.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5 relative">
        <button onClick={onDismiss} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Set up interview scheduling</h3>
        </div>
        <p className="text-sm text-gray-600">Connect your Calendly link so applicants can self-schedule interviews.</p>
        <p className="text-xs text-gray-500">
          Don't have Calendly?{' '}
          <a href="https://calendly.com/signup" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-semibold">
            Create a free account
          </a>
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Calendly URL</label>
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="url"
              value={calendlyUrl}
              onChange={(e) => setCalendlyUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="https://calendly.com/your-name"
              autoFocus
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <button onClick={onDismiss} className="text-sm text-gray-500 hover:text-gray-700">Skip for now</button>
          <button
            onClick={handleSave}
            disabled={!calendlyUrl.trim() || saving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function App() {
  const { isAuthenticated, isLoading, workspace, token, user, updateWorkspace, logout } = useAuthStore();
  const [workspaceChecked, setWorkspaceChecked] = useState(false);
  const [billingStatus, setBillingStatus] = useState<string | null>(null);
  const [billingChecked, setBillingChecked] = useState(false);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [showCalendlyPrompt, setShowCalendlyPrompt] = useState(false);

  // Fetch workspace data
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setWorkspaceChecked(true);
      setBillingChecked(true);
      return;
    }
    let cancelled = false;
    fetch(`${API_URL}/workspaces/current`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!cancelled && data?.workspace) {
          updateWorkspace(data.workspace);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setWorkspaceChecked(true); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Fetch billing status
  useEffect(() => {
    if (!isAuthenticated || !token || !workspaceChecked) return;
    if (!workspace?.companyName) {
      setBillingChecked(true);
      return;
    }

    // Grandfathered check (client-side fast check)
    if (user?.email && GRANDFATHERED_EMAILS.includes(user.email.toLowerCase())) {
      setBillingStatus('grandfathered');
      setBillingChecked(true);
      return;
    }

    let cancelled = false;
    fetch(`${API_URL}/api/billing`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!cancelled && data) {
          setBillingStatus(data.grandfathered ? 'grandfathered' : (data.status || 'none'));
        }
      })
      .catch(() => setBillingStatus('none'))
      .finally(() => { if (!cancelled) setBillingChecked(true); });
    return () => { cancelled = true; };
  }, [isAuthenticated, token, workspaceChecked, workspace?.companyName]);

  // Calendly prompt
  useEffect(() => {
    if (workspace?.companyName && !workspace?.calendlyLink) {
      const dismissed = localStorage.getItem('calendly-prompt-dismissed');
      if (!dismissed) setShowCalendlyPrompt(true);
    } else {
      setShowCalendlyPrompt(false);
    }
  }, [workspace]);

  const handleCalendlySave = useCallback(async (link: string) => {
    try {
      await fetch(`${API_URL}/workspaces/current`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ calendlyLink: link }),
      });
      updateWorkspace({ calendlyLink: link });
      setShowCalendlyPrompt(false);
    } catch (err) {
      console.error('Failed to save Calendly link:', err);
    }
  }, [token, updateWorkspace]);

  const handleCalendlyDismiss = useCallback(() => {
    localStorage.setItem('calendly-prompt-dismissed', 'true');
    setShowCalendlyPrompt(false);
  }, []);

  const handleCheckout = async () => {
    setCheckoutPending(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to create checkout session');
        setCheckoutPending(false);
      }
    } catch {
      alert('Failed to connect to billing. Please try again.');
      setCheckoutPending(false);
    }
  };

  // Handle Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      setBillingStatus('trialing');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Loading
  if (isLoading || (isAuthenticated && (!workspaceChecked || !billingChecked))) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Onboarding (company name)
  if (!workspace?.companyName) {
    return <OnboardingPage />;
  }

  // PAYWALL: Must have active subscription, trial, or be grandfathered
  const hasAccess = billingStatus === 'active' || billingStatus === 'trialing' || billingStatus === 'grandfathered';
  if (!hasAccess) {
    return <PaywallPage onCheckout={handleCheckout} isPending={checkoutPending} onLogout={logout} />;
  }

  // Full app access
  return (
    <Layout>
      {showCalendlyPrompt && (
        <CalendlyPromptModal onSave={handleCalendlySave} onDismiss={handleCalendlyDismiss} />
      )}
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/conversations" element={<ConversationsPage />} />
        <Route path="/conversations/:applicantId" element={<ConversationDetailPage />} />
        <Route path="/applicants" element={<ApplicantsPage />} />
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/forms/new" element={<FormBuilderPage />} />
        <Route path="/forms/:id" element={<FormBuilderPage />} />
        <Route path="/forms/:id/submissions" element={<FormSubmissionsPage />} />
        <Route path="/email" element={<EmailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
